import { Compass, PackageX, Store, MousePointerClick, Unplug, EyeOff } from "lucide-react";
import { ACCENT, Eyebrow, H2, MarketplaceLogos } from "@/components/ecommerce/shared";

export function EcomExperience() {
  return (
    <section id="experience" aria-labelledby="experience-heading" data-testid="ecom-experience-section" className="relative py-20 sm:py-24 bg-white border-y border-zinc-200">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>eCommerce enablement South Africa</Eyebrow>
          <H2 id="experience-heading">Helping South African Businesses Sell Online Since 2020</H2>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            Over 5,000 South African SMEs have been empowered through Rekisa's eCommerce solutions, programmes and support. Our
            experience includes helping product businesses develop their online selling capabilities and supporting sellers across
            major South African marketplaces.
          </p>
        </div>
        <div className="mt-12 grid lg:grid-cols-[0.8fr_1.2fr] gap-5">
          <div data-testid="ecom-proof-sme" className="rounded-3xl bg-[#16120F] text-white p-8 flex flex-col justify-between">
            <div className="font-display text-6xl sm:text-7xl font-black tracking-[-0.04em]" style={{ color: ACCENT }}>5,000+</div>
            <p className="mt-4 text-white/80 text-lg leading-snug">South African SMEs empowered with eCommerce solutions since 2020.</p>
          </div>
          <div data-testid="ecom-proof-marketplaces" className="rounded-3xl border border-zinc-200 bg-[#FBF7F2] p-8">
            <h3 className="font-display text-xl font-black text-zinc-900">Marketplace sellers we've supported</h3>
            <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
              We help sellers prepare their products, catalogues and operations for marketplaces including Amazon, Takealot and Makro.
            </p>
            <div className="mt-6"><MarketplaceLogos testid="ecom-logos-experience" compact /></div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PROBLEMS = [
  { Icon: Compass, title: "Not sure where to start", copy: "The business needs a clear strategy and an appropriate selling channel." },
  { Icon: Store, title: "An online store that isn't ready to sell", copy: "Products, payments, checkout or delivery are incomplete or poorly configured." },
  { Icon: PackageX, title: "Struggling with marketplaces", copy: "Seller onboarding, catalogues, listings and operational requirements are difficult to manage." },
  { Icon: MousePointerClick, title: "Getting traffic without enough purchases", copy: "Customers visit, but product information, the shopping experience or checkout creates friction." },
  { Icon: Unplug, title: "Disconnected tools and processes", copy: "Marketing, order fulfilment and customer communication are managed separately." },
  { Icon: EyeOff, title: "Limited visibility into performance", copy: "The business cannot clearly see which products, channels and activities contribute to results." },
];

export function EcomChallenges() {
  return (
    <section id="challenges" aria-labelledby="challenges-heading" data-testid="ecom-challenges-section" className="relative py-20 sm:py-28 bg-[#FBF7F2]">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Selling online in South Africa</Eyebrow>
          <H2 id="challenges-heading">Selling Online Takes More Than Having a Website</H2>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            Many businesses have great products but struggle to turn them into consistent online sales. Some don't know which
            selling channel to start with. Others have online stores but struggle with product setup, payments, delivery, customer
            acquisition or marketplace operations.
          </p>
          <p className="mt-3 text-zinc-600 leading-relaxed">When these parts don't work together, selling online becomes complicated and expensive.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROBLEMS.map(({ Icon, title, copy }, i) => (
            <div key={title} data-testid={`ecom-challenge-${i}`} className="rounded-2xl border border-zinc-200 bg-white p-6">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}14`, color: ACCENT }}><Icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-lg font-black text-zinc-900 leading-snug">{title}</h3>
              <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
        <p data-testid="ecom-challenges-transition" className="mt-12 font-display text-2xl sm:text-3xl font-black tracking-[-0.02em] text-zinc-900 max-w-3xl">
          Rekisa connects the pieces of your eCommerce business <span style={{ color: ACCENT }}>so they work together.</span>
        </p>
      </div>
    </section>
  );
}
